import "./AtomNetwork.scss";

import { useCallback, useEffect, useRef } from "react";

interface Node {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  orbiting: boolean;
  orbitRadius: number;
  orbitSpeed: number;
  orbitAngle: number;
  orbitCenterX: number;
  orbitCenterY: number;
  pulse: number;
  pulseSpeed: number;
}

interface Ripple {
  x: number;
  y: number;
  radius: number;
  speed: number;
  life: number;
  maxLife: number;
}

const ACCENT = { r: 0, g: 245, b: 160 };
const ACCENT2 = { r: 0, g: 180, b: 255 };
const MAX_RIPPLES = 6;
const RIPPLE_COOLDOWN = 130;

const getResponsiveConfig = (width: number) => {
  if (width < 480)
    return {
      nodeCount: 22,
      electronCount: 3,
      connectionDistance: 130,
      hexCount: 1,
    };
  if (width < 768)
    return {
      nodeCount: 34,
      electronCount: 5,
      connectionDistance: 160,
      hexCount: 2,
    };
  if (width < 1024)
    return {
      nodeCount: 48,
      electronCount: 7,
      connectionDistance: 185,
      hexCount: 2,
    };
  return {
    nodeCount: 68,
    electronCount: 10,
    connectionDistance: 210,
    hexCount: 3,
  };
};

const AtomNetwork = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const nodesRef = useRef<Node[]>([]);
  const electronsRef = useRef<Node[]>([]);
  const ripplesRef = useRef<Ripple[]>([]);
  const mouseRef = useRef({ x: -1000, y: -1000 });
  const rafRef = useRef(0);
  const timeRef = useRef(0);
  const lastRippleAtRef = useRef(0);

  const configRef = useRef(getResponsiveConfig(window.innerWidth));

  const initNodes = useCallback((width: number, height: number) => {
    configRef.current = getResponsiveConfig(width);
    const { nodeCount } = configRef.current;
    const nodes: Node[] = [];
    for (let i = 0; i < nodeCount; i++) {
      const orbiting = Math.random() > 0.66;
      nodes.push({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.32,
        vy: (Math.random() - 0.5) * 0.32,
        radius: Math.random() * 2.4 + 1.2,
        orbiting,
        orbitRadius: Math.random() * 34 + 16,
        orbitSpeed:
          (Math.random() * 0.6 + 0.34) * (Math.random() > 0.5 ? 1 : -1),
        orbitAngle: Math.random() * Math.PI * 2,
        orbitCenterX: Math.random() * width,
        orbitCenterY: Math.random() * height,
        pulse: Math.random() * Math.PI * 2,
        pulseSpeed: Math.random() * 0.025 + 0.012,
      });
    }

    const electrons: Node[] = [];
    for (let i = 0; i < configRef.current.electronCount; i++) {
      electrons.push({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 1.5,
        vy: (Math.random() - 0.5) * 1.5,
        radius: Math.random() * 1.4 + 0.8,
        orbiting: false,
        orbitRadius: 0,
        orbitSpeed: 0,
        orbitAngle: 0,
        orbitCenterX: 0,
        orbitCenterY: 0,
        pulse: Math.random() * Math.PI * 2,
        pulseSpeed: Math.random() * 0.045 + 0.024,
      });
    }

    nodesRef.current = nodes;
    electronsRef.current = electrons;
    ripplesRef.current = [];
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    let lastWidth = 0;
    let resizeTimer = 0;

    const resize = (forceReinit = false) => {
      const parent = canvas.parentElement;
      if (!parent) return;
      const dpr = window.devicePixelRatio || 1;
      const w = parent.clientWidth;
      const h = parent.clientHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      const ctx = canvas.getContext("2d");
      if (ctx) ctx.scale(dpr, dpr);
      if (forceReinit || Math.abs(w - lastWidth) > 16) {
        lastWidth = w;
        initNodes(w, h);
      }
    };

    // One animation frame. Declared inside the effect so it can mutate the
    // scene refs and reschedule itself (React forbids both in a hook callback).
    const draw = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      const width = canvas.width;
      const height = canvas.height;
      const time = timeRef.current;

      ctx.clearRect(0, 0, width, height);

      const nodes = nodesRef.current;
      const electrons = electronsRef.current;
      const ripples = ripplesRef.current;
      const mouse = mouseRef.current;

      // Update ripples first so atoms can react to their current edge.
      for (let i = ripples.length - 1; i >= 0; i--) {
        const ripple = ripples[i];
        ripple.radius += ripple.speed;
        ripple.life += 1;
        if (ripple.life >= ripple.maxLife) {
          ripples.splice(i, 1);
        }
      }

      // Update nodes
      for (const node of nodes) {
        if (node.orbiting) {
          node.orbitAngle += node.orbitSpeed * 0.01;
          node.orbitCenterX += node.vx;
          node.orbitCenterY += node.vy;
          node.x =
            node.orbitCenterX + Math.cos(node.orbitAngle) * node.orbitRadius;
          node.y =
            node.orbitCenterY + Math.sin(node.orbitAngle) * node.orbitRadius;

          if (node.orbitCenterX < -20 || node.orbitCenterX > width + 20)
            node.vx *= -1;
          if (node.orbitCenterY < -20 || node.orbitCenterY > height + 20)
            node.vy *= -1;
        } else {
          node.x += node.vx;
          node.y += node.vy;

          if (node.x < -20 || node.x > width + 20) node.vx *= -1;
          if (node.y < -20 || node.y > height + 20) node.vy *= -1;
        }

        // Mouse repulsion
        const dx = node.x - mouse.x;
        const dy = node.y - mouse.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < 170 && dist > 0) {
          const force = ((170 - dist) / 170) * 0.62;
          node.x += (dx / dist) * force;
          node.y += (dy / dist) * force;
        }

        // Ripple edge force for wave effect.
        for (const ripple of ripples) {
          const rx = node.x - ripple.x;
          const ry = node.y - ripple.y;
          const rippleDist = Math.sqrt(rx * rx + ry * ry);
          const edgeDistance = Math.abs(rippleDist - ripple.radius);

          if (edgeDistance < 24 && rippleDist > 0) {
            const edgeForce = (1 - edgeDistance / 24) * 0.75;
            node.x += (rx / rippleDist) * edgeForce;
            node.y += (ry / rippleDist) * edgeForce;
          }
        }

        node.pulse += node.pulseSpeed;
      }

      // Update electrons
      for (const electron of electrons) {
        electron.x += electron.vx;
        electron.y += electron.vy;
        electron.pulse += electron.pulseSpeed;

        if (electron.x < -10 || electron.x > width + 10) electron.vx *= -1;
        if (electron.y < -10 || electron.y > height + 10) electron.vy *= -1;
      }

      // Draw connections
      const { connectionDistance, hexCount } = configRef.current;
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < connectionDistance) {
            const opacity = (1 - dist / connectionDistance) * 0.26;
            const color = i % 3 === 0 ? ACCENT2 : ACCENT;
            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.strokeStyle = `rgba(${color.r},${color.g},${color.b},${opacity})`;
            ctx.lineWidth = 0.7;
            ctx.stroke();
          }
        }
      }

      // Draw ripple circles.
      for (const ripple of ripples) {
        const progress = ripple.life / ripple.maxLife;
        const opacity = (1 - progress) * 0.28;

        ctx.beginPath();
        ctx.arc(ripple.x, ripple.y, ripple.radius, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(${ACCENT2.r},${ACCENT2.g},${ACCENT2.b},${opacity})`;
        ctx.lineWidth = 1;
        ctx.stroke();
      }

      // Draw orbital rings for orbiting nodes
      for (const node of nodes) {
        if (node.orbiting) {
          const ringOpacity =
            0.06 + Math.sin(time * 0.001 + node.orbitAngle) * 0.025;
          ctx.beginPath();
          ctx.arc(
            node.orbitCenterX,
            node.orbitCenterY,
            node.orbitRadius,
            0,
            Math.PI * 2,
          );
          ctx.strokeStyle = `rgba(${ACCENT.r},${ACCENT.g},${ACCENT.b},${ringOpacity})`;
          ctx.lineWidth = 0.55;
          ctx.stroke();
        }
      }

      // Draw nodes (atoms)
      for (const node of nodes) {
        const pulseScale = 1 + Math.sin(node.pulse) * 0.32;
        const r = node.radius * pulseScale;
        const color = node.orbiting ? ACCENT2 : ACCENT;
        const glowOpacity = 0.12 + Math.sin(node.pulse) * 0.05;

        // Glow
        const gradient = ctx.createRadialGradient(
          node.x,
          node.y,
          0,
          node.x,
          node.y,
          r * 7,
        );
        gradient.addColorStop(
          0,
          `rgba(${color.r},${color.g},${color.b},${glowOpacity})`,
        );
        gradient.addColorStop(1, `rgba(${color.r},${color.g},${color.b},0)`);
        ctx.beginPath();
        ctx.arc(node.x, node.y, r * 7, 0, Math.PI * 2);
        ctx.fillStyle = gradient;
        ctx.fill();

        // Core
        const coreOpacity = 0.54 + Math.sin(node.pulse) * 0.22;
        ctx.beginPath();
        ctx.arc(node.x, node.y, r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${color.r},${color.g},${color.b},${coreOpacity})`;
        ctx.fill();
      }

      // Draw electrons (small fast particles)
      for (const electron of electrons) {
        const opacity = 0.56 + Math.sin(electron.pulse) * 0.3;
        const trailLength = 10;

        // Trail
        ctx.beginPath();
        ctx.moveTo(electron.x, electron.y);
        ctx.lineTo(
          electron.x - electron.vx * trailLength,
          electron.y - electron.vy * trailLength,
        );
        const trailGradient = ctx.createLinearGradient(
          electron.x,
          electron.y,
          electron.x - electron.vx * trailLength,
          electron.y - electron.vy * trailLength,
        );
        trailGradient.addColorStop(
          0,
          `rgba(${ACCENT.r},${ACCENT.g},${ACCENT.b},${opacity * 0.45})`,
        );
        trailGradient.addColorStop(
          1,
          `rgba(${ACCENT.r},${ACCENT.g},${ACCENT.b},0)`,
        );
        ctx.strokeStyle = trailGradient;
        ctx.lineWidth = electron.radius;
        ctx.stroke();

        // Core
        ctx.beginPath();
        ctx.arc(electron.x, electron.y, electron.radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${opacity * 0.86})`;
        ctx.fill();
      }

      // Draw hexagonal structures faintly
      const hexTime = time * 0.0003;
      for (let i = 0; i < hexCount; i++) {
        const cx = width * (0.56 + i * 0.16) + Math.sin(hexTime + i) * 20;
        const cy =
          height * (0.26 + i * 0.22) + Math.cos(hexTime + i * 1.5) * 15;
        const size = 40 + i * 16;
        const hexOpacity = 0.036 + Math.sin(hexTime + i * 2) * 0.015;

        ctx.beginPath();
        for (let s = 0; s < 6; s++) {
          const angle = (Math.PI / 3) * s - Math.PI / 6 + hexTime * 0.24;
          const hx = cx + Math.cos(angle) * size;
          const hy = cy + Math.sin(angle) * size;
          if (s === 0) ctx.moveTo(hx, hy);
          else ctx.lineTo(hx, hy);
        }
        ctx.closePath();
        ctx.strokeStyle = `rgba(${ACCENT.r},${ACCENT.g},${ACCENT.b},${hexOpacity})`;
        ctx.lineWidth = 0.9;
        ctx.stroke();
      }

      timeRef.current += 16;
      rafRef.current = requestAnimationFrame(draw);
    };

    resize(true);
    rafRef.current = requestAnimationFrame(draw);

    const onResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => resize(false), 150);
    };

    const onMouseMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      mouseRef.current = { x, y };

      const now = performance.now();
      if (now - lastRippleAtRef.current > RIPPLE_COOLDOWN) {
        if (ripplesRef.current.length >= MAX_RIPPLES) {
          ripplesRef.current.shift();
        }

        ripplesRef.current.push({
          x,
          y,
          radius: 8,
          speed: 2.8 + Math.random() * 1.6,
          life: 0,
          maxLife: 44 + Math.floor(Math.random() * 18),
        });
        lastRippleAtRef.current = now;
      }
    };

    const onMouseLeave = () => {
      mouseRef.current = { x: -1000, y: -1000 };
      ripplesRef.current = [];
    };

    window.addEventListener("resize", onResize);
    window.addEventListener("mousemove", onMouseMove);
    canvas.addEventListener("mouseleave", onMouseLeave);

    return () => {
      cancelAnimationFrame(rafRef.current);
      clearTimeout(resizeTimer);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("mousemove", onMouseMove);
      canvas.removeEventListener("mouseleave", onMouseLeave);
    };
  }, [initNodes]);

  return <canvas ref={canvasRef} className="atomNetwork" />;
};

export default AtomNetwork;
